import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Connection,
  ConnectionMode,
} from 'reactflow';
import toast from 'react-hot-toast';

import { ConversationFlow, FlowNode, ReactFlowNode, ReactFlowEdge } from '../types';
import { FlowAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import NodeEditPanel from './NodeEditPanel';
import FlowTestPanel from './FlowTestPanel';
import CustomNode from './CustomNode';
import { ArrowLeftIcon, PlayIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Registra os tipos de nó customizados
const nodeTypes = {
  custom: CustomNode,
};

const FlowEditor = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  
  const [flow, setFlow] = useState<ConversationFlow | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);

  useEffect(() => {
    if (flowId) {
      loadFlow(flowId);
    }
  }, [flowId]);

  useEffect(() => {
    updateFlowFromReactFlow();
  }, [nodes, edges]);

  const loadFlow = async (id: string) => {
    try {
      setIsLoading(true);
      const loadedFlow = await FlowAPI.getFlow(id);
      setFlow(loadedFlow);
      convertFlowToReactFlow(loadedFlow);
      await validateCurrentFlow(id);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar fluxo');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const validateCurrentFlow = async (id: string) => {
    try {
      const result = await FlowAPI.validateFlow(id);
      setValidationResult(result);
      
      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} erro(s) encontrado(s) no fluxo`);
      } else if (result.warnings?.length > 0) {
        toast(`${result.warnings.length} aviso(s) no fluxo`, { icon: '⚠️' });
      }
    } catch (error) {
      console.error('Erro ao validar fluxo:', error);
    }
  };

  const convertFlowToReactFlow = (conversationFlow: ConversationFlow) => {
    // Converte nós
    const reactNodes: ReactFlowNode[] = conversationFlow.nodes.map((node) => ({
      id: node.id,
      type: 'custom',
      position: node.position,
      data: {
        label: node.title || node.text?.substring(0, 30) || `Nó ${node.type}`,
        flowNode: node,
        onEdit: handleEditNode,
        onDelete: handleDeleteNode,
      },
      draggable: true,
      selectable: true,
    }));

    // Converte edges baseado nas conexões entre nós
    const reactEdges: ReactFlowEdge[] = [];
    
    conversationFlow.nodes.forEach((node) => {
      // Edge para next_node direto
      if (node.next_node) {
        reactEdges.push({
          id: `${node.id}-${node.next_node}`,
          source: node.id,
          target: node.next_node,
          type: 'default',
          animated: false,
        });
      }

      // Edges para opções
      node.options.forEach((option, index) => {
        if (option.next_node) {
          reactEdges.push({
            id: `${node.id}-option-${index}-${option.next_node}`,
            source: node.id,
            target: option.next_node,
            type: 'default',
            label: option.label,
            animated: true,
          });
        }
      });

      // Edges para condições
      node.conditions.forEach((condition, index) => {
        if (condition.next_node) {
          reactEdges.push({
            id: `${node.id}-condition-${index}-${condition.next_node}`,
            source: node.id,
            target: condition.next_node,
            type: 'default',
            label: `${condition.variable} ${condition.operator}`,
            animated: true,
          });
        }
      });
    });

    setNodes(reactNodes);
    setEdges(reactEdges);
  };

  const updateFlowFromReactFlow = () => {
    if (!flow) return;

    const updatedNodes: FlowNode[] = nodes.map((reactNode) => ({
      ...reactNode.data.flowNode,
      position: reactNode.position,
    }));

    setFlow({
      ...flow,
      nodes: updatedNodes,
    });
  };

  const onConnect = useCallback(
    (params: Connection) => {
      if (params.source && params.target) {
        // Adiciona edge visual
        setEdges((eds) => addEdge(params, eds));
        
        // Atualiza o fluxo lógico
        const sourceNode = nodes.find((n) => n.id === params.source);
        if (sourceNode) {
          const updatedNode: FlowNode = {
            ...sourceNode.data.flowNode,
            next_node: params.target,
          };
          
          handleUpdateNode(updatedNode);
        }
      }
    },
    [nodes]
  );

  const handleSave = async () => {
    if (!flow || !flowId) return;

    try {
      setIsSaving(true);
      await FlowAPI.updateFlow(flowId, flow);
      toast.success('Fluxo salvo com sucesso!');
      await validateCurrentFlow(flowId);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar fluxo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNode = (nodeType: string) => {
    if (!flow) return;

    const newNode: FlowNode = {
      id: `node_${Date.now()}`,
      type: nodeType as any,
      title: `Novo ${nodeType}`,
      text: nodeType === 'message' ? 'Digite sua mensagem aqui...' : 
            nodeType === 'question' ? 'Faça sua pergunta aqui...' : '',
      options: nodeType === 'question' ? [
        { label: 'Opção 1', value: 'opt1' },
        { label: 'Opção 2', value: 'opt2' }
      ] : [],
      conditions: nodeType === 'condition' ? [
        { variable: 'variavel', operator: 'equals', value: 'valor' }
      ] : [],
      position: { 
        x: Math.random() * 500 + 100, 
        y: Math.random() * 300 + 100 
      },
    };

    const newReactNode: ReactFlowNode = {
      id: newNode.id,
      type: 'custom',
      position: newNode.position,
      data: {
        label: newNode.title || `Novo ${nodeType}`,
        flowNode: newNode,
        onEdit: handleEditNode,
        onDelete: handleDeleteNode,
      },
    };

    setNodes((nds) => [...nds, newReactNode]);
    setSelectedNode(newNode);
  };

  const handleEditNode = (node: FlowNode) => {
    setSelectedNode(node);
  };

  const handleUpdateNode = (updatedNode: FlowNode) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === updatedNode.id
          ? {
              ...n,
              data: {
                ...n.data,
                label: updatedNode.title || updatedNode.text?.substring(0, 30) || `Nó ${updatedNode.type}`,
                flowNode: updatedNode,
              },
            }
          : n
      )
    );
  };

  const handleDeleteNode = (nodeId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este nó?')) {
      return;
    }

    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Carregando fluxo...</p>
        </div>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Fluxo não encontrado</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen-minus-header bg-white">
      {/* Toolbar */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Voltar
            </button>
          </div>
          
          <h2 className="text-lg font-semibold text-gray-900 mt-3 mb-1">
            {flow.metadata.name}
          </h2>
          
          {flow.metadata.description && (
            <p className="text-sm text-gray-600">
              {flow.metadata.description}
            </p>
          )}
        </div>

        {/* Validation Status */}
        {validationResult && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              {validationResult.is_valid ? (
                <>
                  <CheckIcon className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-600 font-medium">
                    Fluxo válido
                  </span>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                  <span className="text-sm text-red-600 font-medium">
                    {validationResult.errors?.length || 0} erro(s)
                  </span>
                </>
              )}
            </div>
            
            {validationResult.warnings?.length > 0 && (
              <p className="text-xs text-yellow-600 mt-1">
                {validationResult.warnings.length} aviso(s)
              </p>
            )}
          </div>
        )}

        {/* Add Node Buttons */}
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Adicionar Nó
          </h3>
          
          <div className="space-y-2">
            <button
              onClick={() => handleAddNode('message')}
              className="w-full text-left p-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
            >
              💬 Mensagem
            </button>
            
            <button
              onClick={() => handleAddNode('question')}
              className="w-full text-left p-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"  
            >
              ❓ Pergunta
            </button>
            
            <button
              onClick={() => handleAddNode('input')}
              className="w-full text-left p-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
            >
              ✏️ Entrada
            </button>
            
            <button
              onClick={() => handleAddNode('condition')}
              className="w-full text-left p-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
            >
              🔀 Condição
            </button>
            
            <button
              onClick={() => handleAddNode('end')}
              className="w-full text-left p-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
            >
              🏁 Fim
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 space-y-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? (
              <>
                <LoadingSpinner size="small" color="white" />
                <span className="ml-2">Salvando...</span>
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4 mr-2" />
                Salvar Fluxo
              </>
            )}
          </button>

          <button
            onClick={() => setShowTestPanel(true)}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <PlayIcon className="w-4 h-4 mr-2" />
            Testar Fluxo
          </button>
        </div>
      </div>

      {/* Main Editor */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          fitView
          className="bg-gray-50"
        >
          <Controls />
          <MiniMap 
            nodeColor={(node) => {
              switch (node.data.flowNode.type) {
                case 'message': return '#6B7280';
                case 'question': return '#3B82F6';
                case 'condition': return '#F59E0B';
                case 'input': return '#10B981';
                case 'end': return '#EF4444';
                default: return '#6B7280';
              }
            }}
          />
          <Background variant="dots" gap={20} size={1} />
        </ReactFlow>
      </div>

      {/* Side Panels */}
      {selectedNode && (
        <NodeEditPanel
          node={selectedNode}
          onUpdateNode={handleUpdateNode}
          onClose={() => setSelectedNode(null)}
        />
      )}

      {showTestPanel && flowId && (
        <FlowTestPanel
          flowId={flowId}
          onClose={() => setShowTestPanel(false)}
        />
      )}
    </div>
  );
};

export default FlowEditor;